// src/components/common/Header/Header.js
import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Box, IconButton,
  Menu, MenuItem, ListItemIcon, ListItemText, Divider,
  Drawer, List, ListItem, ListItemButton,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';
import { useAuth } from '../../../contexts/AuthContext';
import UserAvatar from '../UserAvatar/UserAvatar';

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Demo', path: '/demo' },
  { label: 'Pricing', path: '/pricing' },
  { label: 'About', path: '/about-us' },
  { label: 'Contact', path: '/contact' },
];

const NAV_BTN_SX = (active) => ({
  color: active ? '#FFFFFF' : 'rgba(255,255,255,0.5)',
  fontWeight: active ? 600 : 400,
  fontSize: '14px',
  px: 1.75,
  py: 0.75,
  borderRadius: '6px',
  textTransform: 'none',
  fontFamily: 'Inter, sans-serif',
  letterSpacing: '-0.01em',
  minWidth: 'unset',
  position: 'relative',
  '&:hover': { color: '#FFFFFF', backgroundColor: 'rgba(255,255,255,0.04)' },
  ...(active && {
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '3px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '14px',
      height: '1.5px',
      background: '#FFFFFF',
      borderRadius: '1px',
      opacity: 0.6,
    },
  }),
});

const Header = ({ onSignInClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [appMenuAnchor, setAppMenuAnchor] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (path) => location.pathname === path;
  const handleNav = (path) => { navigate(path); setMobileOpen(false); };

  const appActive = isActive('/dubbing') || isActive('/audio-translation');

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: scrolled ? 'rgba(0,0,0,0.92)' : 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(16px)',
          borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : '1px solid transparent',
          boxShadow: 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 4 }, minHeight: '64px !important' }}>

          {/* Logo */}
          <Box
            onClick={() => navigate('/')}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
          >
            <Typography sx={{
              fontWeight: 800, fontSize: '20px', letterSpacing: '-0.04em',
              fontFamily: 'Inter, sans-serif', lineHeight: 1,
            }}>
              <Box component="span" sx={{ color: '#FFFFFF' }}>Dub</Box>
              <Box component="span" sx={{ color: 'rgba(255,255,255,0.4)' }}>Labs</Box>
            </Typography>
          </Box>

          {/* Desktop nav — centered */}
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 0,
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
          }}>
            {NAV_LINKS.map((link) => (
              <Button key={link.path} onClick={() => handleNav(link.path)} sx={NAV_BTN_SX(isActive(link.path))}>
                {link.label}
              </Button>
            ))}

            {/* App dropdown — only for authenticated users */}
            {isAuthenticated && (
              <>
                <Button
                  onClick={(e) => setAppMenuAnchor(e.currentTarget)}
                  endIcon={
                    <KeyboardArrowDownIcon
                      sx={{
                        fontSize: '16px !important',
                        transition: 'transform 0.2s ease',
                        transform: Boolean(appMenuAnchor) ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  }
                  sx={{
                    ...NAV_BTN_SX(appActive),
                    color: appActive ? '#FFFFFF' : Boolean(appMenuAnchor) ? '#FFFFFF' : 'rgba(255,255,255,0.5)',
                    backgroundColor: Boolean(appMenuAnchor) ? 'rgba(255,255,255,0.04)' : 'transparent',
                  }}
                >
                  Dashboard
                </Button>

                <Menu
                  anchorEl={appMenuAnchor}
                  open={Boolean(appMenuAnchor)}
                  onClose={() => setAppMenuAnchor(null)}
                  PaperProps={{
                    sx: {
                      backgroundColor: '#111111',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '10px',
                      minWidth: '200px',
                      mt: 0.75,
                      boxShadow: '0 16px 48px rgba(0,0,0,0.8)',
                      p: 0.5,
                    },
                  }}
                  transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
                >
                  <MenuItem
                    onClick={() => { setAppMenuAnchor(null); navigate('/dubbing'); }}
                    sx={{
                      borderRadius: '7px', px: 1.5, py: 1.25, gap: 1.25,
                      backgroundColor: isActive('/dubbing') ? 'rgba(255,255,255,0.06)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                    }}
                  >
                    <Box sx={{
                      width: 28, height: 28, borderRadius: '7px',
                      backgroundColor: 'rgba(255,255,255,0.06)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <RecordVoiceOverIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 500, lineHeight: 1.2 }}>
                        Video Dubbing
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif', fontSize: '11px', mt: 0.25 }}>
                        Dub videos into any language
                      </Typography>
                    </Box>
                  </MenuItem>

                  <MenuItem
                    onClick={() => { setAppMenuAnchor(null); navigate('/audio-translation'); }}
                    sx={{
                      borderRadius: '7px', px: 1.5, py: 1.25, gap: 1.25,
                      backgroundColor: isActive('/audio-translation') ? 'rgba(255,255,255,0.06)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                    }}
                  >
                    <Box sx={{
                      width: 28, height: 28, borderRadius: '7px',
                      backgroundColor: 'rgba(255,255,255,0.06)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <MusicNoteIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 500, lineHeight: 1.2 }}>
                        Audio Translation
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif', fontSize: '11px', mt: 0.25 }}>
                        Translate audio files
                      </Typography>
                    </Box>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>

          {/* Right side */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isAuthenticated ? (
              <>
                <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)} sx={{ p: 0 }}>
                  <UserAvatar email={user?.email} size={32} />
                </IconButton>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={() => setUserMenuAnchor(null)}
                  PaperProps={{
                    sx: {
                      backgroundColor: '#111111',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '10px',
                      minWidth: '210px',
                      mt: 1,
                      boxShadow: '0 16px 48px rgba(0,0,0,0.8)',
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '13px', fontFamily: 'Inter, sans-serif' }}>
                      {user?.email}
                    </Typography>
                    <Typography sx={{ color: '#555', fontSize: '12px', textTransform: 'capitalize', fontFamily: 'Inter, sans-serif' }}>
                      {user?.role}
                    </Typography>
                  </Box>
                  <MenuItem onClick={() => { setUserMenuAnchor(null); navigate('/dubbing'); }}>
                    <ListItemIcon><DashboardIcon sx={{ fontSize: 16, color: '#555' }} /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ sx: { fontSize: '14px', fontFamily: 'Inter, sans-serif' } }}>Video Dubbing</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => { setUserMenuAnchor(null); navigate('/audio-translation'); }}>
                    <ListItemIcon><MusicNoteIcon sx={{ fontSize: 16, color: '#555' }} /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ sx: { fontSize: '14px', fontFamily: 'Inter, sans-serif' } }}>Audio Translation</ListItemText>
                  </MenuItem>
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 0.5 }} />
                  <MenuItem onClick={() => { setUserMenuAnchor(null); logout(); }}>
                    <ListItemIcon><LogoutIcon sx={{ fontSize: 16, color: '#EF4444' }} /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ sx: { fontSize: '14px', fontFamily: 'Inter, sans-serif', color: '#EF4444' } }}>Logout</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              // Primary marketing CTA. DubLabs is a productized service
              // (not self-serve SaaS) — CTA routes to /contact for a
              // free sample dub rather than to sign-in for account
              // creation. Existing customers can still reach /signin
              // directly via URL or the footer link.
              <Button
                onClick={() => navigate('/contact')}
                component={motion.button}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  backgroundColor: '#FFFFFF',
                  color: '#000000',
                  fontWeight: 700,
                  fontSize: '14px',
                  px: 2.5,
                  py: 0.85,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  letterSpacing: '-0.01em',
                  '&:hover': { backgroundColor: '#F0F0F0' },
                }}
              >
                Get in Touch
              </Button>
            )}

            {/* Mobile hamburger */}
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{ display: { xs: 'flex', md: 'none' }, color: 'rgba(255,255,255,0.7)', ml: 0.5 }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: 260,
            backgroundColor: '#0A0A0A',
            borderLeft: '1px solid rgba(255,255,255,0.06)',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '18px', fontFamily: 'Inter, sans-serif', letterSpacing: '-0.04em' }}>
            <Box component="span" sx={{ color: '#fff' }}>Dub</Box>
            <Box component="span" sx={{ color: 'rgba(255,255,255,0.35)' }}>Labs</Box>
          </Typography>
          <IconButton onClick={() => setMobileOpen(false)} sx={{ color: 'rgba(255,255,255,0.4)' }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <List sx={{ pt: 1.5, px: 1 }}>
          {NAV_LINKS.map((link) => (
            <ListItem key={link.path} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => handleNav(link.path)}
                sx={{
                  px: 2, py: 1.25, borderRadius: '8px',
                  color: isActive(link.path) ? '#fff' : 'rgba(255,255,255,0.5)',
                  fontWeight: isActive(link.path) ? 600 : 400,
                  fontFamily: 'Inter, sans-serif', fontSize: '15px',
                  backgroundColor: isActive(link.path) ? 'rgba(255,255,255,0.06)' : 'transparent',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)', color: '#fff' },
                }}
              >
                {link.label}
              </ListItemButton>
            </ListItem>
          ))}

          {/* App links for authenticated users */}
          {isAuthenticated && (
            <>
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 1 }} />
              {[
                { label: 'Video Dubbing', path: '/dubbing', icon: <RecordVoiceOverIcon sx={{ fontSize: 17, mr: 1.25, color: 'inherit' }} /> },
                { label: 'Audio Translation', path: '/audio-translation', icon: <MusicNoteIcon sx={{ fontSize: 17, mr: 1.25, color: 'inherit' }} /> },
              ].map((link) => (
                <ListItem key={link.path} disablePadding sx={{ mb: 0.25 }}>
                  <ListItemButton
                    onClick={() => handleNav(link.path)}
                    sx={{
                      px: 2, py: 1.25, borderRadius: '8px', display: 'flex', alignItems: 'center',
                      color: isActive(link.path) ? '#fff' : 'rgba(255,255,255,0.5)',
                      fontWeight: isActive(link.path) ? 600 : 400,
                      fontFamily: 'Inter, sans-serif', fontSize: '14px',
                      backgroundColor: isActive(link.path) ? 'rgba(255,255,255,0.06)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)', color: '#fff' },
                    }}
                  >
                    {link.icon}
                    {link.label}
                  </ListItemButton>
                </ListItem>
              ))}
            </>
          )}
        </List>

        <Box sx={{ p: 2, mt: 'auto', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          {isAuthenticated ? (
            <Button fullWidth onClick={() => { setMobileOpen(false); logout(); }}
              sx={{ color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', textTransform: 'none', fontWeight: 600, py: 1, fontFamily: 'Inter, sans-serif' }}>
              Logout
            </Button>
          ) : (
            <Button fullWidth onClick={() => { setMobileOpen(false); navigate('/contact'); }}
              sx={{ backgroundColor: '#FFFFFF', color: '#000000', fontWeight: 700, fontSize: '14px', py: 1.25, borderRadius: '8px', textTransform: 'none', fontFamily: 'Inter, sans-serif', '&:hover': { backgroundColor: '#F0F0F0' } }}>
              Get in Touch
            </Button>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default Header;
